import { T, useI18n } from '../lib/i18n'
import { Check, KeyRound, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from '../components/Logo'
import { LoadingScreen } from '../components/UI'
import { useAuth } from '../context/AuthContext'
import {
  clearPendingInvitation,
  formatInvitationCode,
  isValidInvitationCode,
  savePendingInvitation,
} from '../lib/invitations'
import { supabase } from '../lib/supabase'

export function JoinPage() {
  const { code: rawCode = '' } = useParams()
  const { session, profile, loading, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [connecting, setConnecting] = useState(false)
  const code = useMemo(() => formatInvitationCode(rawCode), [rawCode])
  const valid = isValidInvitationCode(code)

  useEffect(() => {
    if (valid) savePendingInvitation(code)
  }, [code, valid])

  if (!valid) return <InviteMessage title="This invitation link is incomplete" message="Ask the cleaning company to create and share a new TidyPing invitation." />
  if (loading) return <LoadingScreen />
  if (!session) return <Navigate to={`/signin?invite=${encodeURIComponent(code)}`} replace />
  if (!profile?.onboarding_completed_at || !profile.default_account_kind) return <Navigate to="/onboarding" replace />
  if (profile.default_account_kind !== 'client') {
    return <InviteMessage title="Open this link with a client account" message="Business accounts cannot accept a client invitation. Sign out and use the client's Apple, Google, or Microsoft account." />
  }

  async function connect() {
    if (connecting) return
    setConnecting(true)
    const { error } = await supabase.rpc('redeem_client_invitation', { invitation_code: code })
    if (error) {
      toast.error(t(error.message))
      setConnecting(false)
      return
    }
    clearPendingInvitation()
    await refreshProfile()
    toast.success(t('Your cleaning company is connected.'))
    navigate('/', { replace: true })
  }

  return (
    <main className="invite-page">
      <section className="invite-connect-card">
        <Logo />
        <span className="invite-connect-card__icon"><KeyRound size={27} /></span>
        <span className="eyebrow"><T>{"Private client invitation"}</T></span>
        <h1><T>{"Connect your cleaning company"}</T></h1>
        <p><T>{"Your cleanings, chat, and invoices will sync from the exact client record the company approved."}</T></p>
        <code>{code}</code>
        <button className="button button--primary" onClick={connect} disabled={connecting}>
          {connecting ? t('Connecting…') : t('Connect my account')}<Check size={17} />
        </button>
        <div className="invite-connect-card__security"><ShieldCheck size={18} /><span><T>{"The code is single-use and does not replace your secure sign-in."}</T></span></div>
      </section>
    </main>
  )
}

function InviteMessage({ title, message }: { title: string; message: string }) {
  const { t } = useI18n()
  return (
    <main className="invite-page">
      <section className="invite-connect-card">
        <Logo />
        <span className="invite-connect-card__icon"><KeyRound size={27} /></span>
        <h1>{t(title)}</h1>
        <p>{t(message)}</p>
        <a className="button button--primary" href="/signin"><T>{"Go to sign in"}</T></a>
      </section>
    </main>
  )
}
